import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { storage, GrocyConfig } from "utils/storage.ts";
import QRScanner from "../camera/QRScanner.tsx";
import { ScanResult } from "hooks/useCameraScanner.ts";
import "./LoginPage.css";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [showManualForm, setShowManualForm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [manualForm, setManualForm] = useState({
    baseUrl: "",
    apiKey: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if already logged in
  useEffect(() => {
    if (storage.hasConfig()) {
      navigate("/consume");
    }
  }, [navigate]);

  const handleQRCodeResult = (scanResult: ScanResult) => {
    try {
      const qrData = scanResult.text;
      // Parse QR code data in format: <baseURL>|<apiKey>
      const parts = qrData.split('|');
      if (parts.length !== 2) {
        throw new Error("Invalid QR code format. Expected: <baseURL>|<apiKey>");
      }

      const [baseUrl, apiKey] = parts;
      
      // Validate URL format
      let normalizedUrl = baseUrl.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = `https://${normalizedUrl}`;
      }
      
      // Remove trailing slash if present
      normalizedUrl = normalizedUrl.replace(/\/$/, '');
      
      // Remove /api from the end if present
      normalizedUrl = normalizedUrl.replace(/\/api$/, '');

      const config: GrocyConfig = {
        baseUrl: normalizedUrl,
        apiKey: apiKey.trim()
      };

      // Test the configuration by making a simple API call
      testGrocyConfig(config).then(() => {
        storage.setConfig(config);
        setSuccess("Configuration saved successfully!");
        setTimeout(() => {
          navigate("/consume");
        }, 1500);
      }).catch((err) => {
        setError(`Configuration test failed: ${err.message}`);
      });

    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message || "Failed to parse QR code data");
    }
  };

  const testGrocyConfig = async (config: GrocyConfig): Promise<void> => {
    const response = await fetch(`${config.baseUrl}/api/system/info`, {
      headers: {
        'GROCY-API-KEY': config.apiKey,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`API test failed: ${response.statusText}`);
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!manualForm.baseUrl.trim() || !manualForm.apiKey.trim()) {
      setError("Please fill in both fields");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Normalize the base URL
      let normalizedUrl = manualForm.baseUrl.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = `https://${normalizedUrl}`;
      }
      
      // Remove trailing slash if present
      normalizedUrl = normalizedUrl.replace(/\/$/, '');
      
      // Remove /api from the end if present
      normalizedUrl = normalizedUrl.replace(/\/api$/, '');

      const config: GrocyConfig = {
        baseUrl: normalizedUrl,
        apiKey: manualForm.apiKey.trim()
      };

      await testGrocyConfig(config);
      storage.setConfig(config);
      setSuccess("Configuration saved successfully!");
      setTimeout(() => {
        navigate("/consume");
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`Configuration test failed: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: 'baseUrl' | 'apiKey', value: string) => {
    setManualForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="login-page">
      <div className="login-page__container">
        <h1 className="login-page__title">Grocy Tool Setup</h1>
        
        <div className="login-page__content">
          <p className="login-page__description">
            Scan a QR code containing your Grocy configuration in the format:
            <br />
            <code>&lt;baseURL&gt;|&lt;apiKey&gt;</code>
          </p>

          <div className="login-page__qr-section">
            <QRScanner
              onScan={handleQRCodeResult}
              className="login-page__camera-scanner"
              videoClassName="login-page__video"
              overlayContent={<p>Camera not active</p>}
            />
          </div>

          <div className="login-page__divider">
            <span>or</span>
          </div>

          {!showManualForm ? (
            <button
              type="button"
              onClick={() => setShowManualForm(true)}
              className="login-page__manual-button"
            >
              Enter Configuration Manually
            </button>
          ) : (
            <form onSubmit={handleManualSubmit} className="login-page__manual-form">
              <div className="login-page__form-group">
                <label htmlFor="baseUrl" className="login-page__form-label">
                  Grocy Base URL
                </label>
                <input
                  type="url"
                  id="baseUrl"
                  value={manualForm.baseUrl}
                  onChange={(e) => handleInputChange('baseUrl', e.target.value)}
                  placeholder="https://your-grocy-instance.com"
                  className="login-page__form-input"
                  required
                />
              </div>
              
              <div className="login-page__form-group">
                <label htmlFor="apiKey" className="login-page__form-label">
                  API Key
                </label>
                <input
                  type="password"
                  id="apiKey"
                  value={manualForm.apiKey}
                  onChange={(e) => handleInputChange('apiKey', e.target.value)}
                  placeholder="Your Grocy API key"
                  className="login-page__form-input"
                  required
                />
              </div>
              
              <div className="login-page__form-actions">
                <button
                  type="button"
                  onClick={() => setShowManualForm(false)}
                  className="login-page__cancel-button"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="login-page__submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Testing...' : 'Save Configuration'}
                </button>
              </div>
            </form>
          )}

          {error && (
            <div className="login-page__error">
              {error}
            </div>
          )}

          {success && (
            <div className="login-page__success">
              {success}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 